import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DetailsDrawer, useQuery, useQueryClient } from 'shared-ui';

import { fetchDodHistory, fetchDodHistoryCsv } from 'api/dod/dod.v2';
import { DodHistoryEntry } from 'api/dod/dod.v2.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useDodFilters } from '../hooks/useDodFilters/useDodFilters';
import { useDodOutlet } from '../hooks/useDodOutlet/useDodOutlet';

import { HistoryDetails } from './HistoryDetails';
import { HistoryTab } from './HistoryTab';
import {
  HISTORY_PAGE_SIZES,
  HistoryRow,
  HistorySource,
  HistoryStatus,
  historyRcpAliases,
  pendingHistoryEntries,
  toHistoryQueryFilter,
  toHistoryRow,
  toHistoryToolbarOptions,
} from './history.helpers';

const SEARCH_DEBOUNCE_MS = 300;

const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const HistoryTabContainer = (): JSX.Element => {
  const { formatMessage } = useLocale();
  const queryClient = useQueryClient();
  const { historyEntries, isSocketConnected, refreshToken, setToolbarOptions, setDetails } = useDodOutlet();
  const { range, refreshMs, filters, resolveRange } = useDodFilters();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(HISTORY_PAGE_SIZES[0]);
  const [source, setSource] = useState<HistorySource | ''>('');
  const [status, setStatus] = useState<HistoryStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<HistoryRow | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  /** Everything the socket delivered after this moment is "new" and stays out of the table. */
  const [flushedAt, setFlushedAt] = useState(() => Date.now());

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const queryFilter = useMemo(
    () => {
      const { fromTs, toTs } = resolveRange();

      return toHistoryQueryFilter({
        shared: { rcp: filters.rcp, nlk: filters.nlk, cmd: filters.cmd, status: filters.status },
        fromTs,
        toTs,
        source,
        status,
        search,
      });
    },
    // The resolved window slides with the clock; `range` is what should trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.rcp, filters.nlk, filters.cmd, filters.status, range, source, status, search, refreshToken, resolveRange],
  );

  // Resolved on every render (each poll re-renders), so a preset window ends now, not at mount;
  // 'Show N new' invalidates and therefore also refetches with a fresh `to`.
  const { fromTs: liveFrom, toTs: liveTo } = resolveRange();

  const {
    data: historyPage,
    isLoading,
    isError,
  } = useQuery([QueryKeyEnum.DOD_HISTORY_V2, queryFilter, page, pageSize], fetchDodHistory, {
    handleErrorManually: true,
    args: { ...queryFilter, from: liveFrom, to: liveTo, page: page + 1, pageSize },
    refetchInterval: refreshMs,
    keepPreviousData: true,
  });

  useQuery([QueryKeyEnum.DOD_HISTORY_V2, 'csv', queryFilter], fetchDodHistoryCsv, {
    handleErrorManually: true,
    args: { ...queryFilter, format: 'csv' as const },
    enabled: isExporting,
    cacheTime: 0,
    onSuccess: (blob: Blob) => {
      downloadBlob(blob, `dod-history-${new Date().toISOString().slice(0, 19)}.csv`);
      setIsExporting(false);
    },
    onError: () => {
      toast.dark(formatMessage({ id: AppMessages['dod.common.error'] }));
      setIsExporting(false);
    },
  });

  const rows = useMemo(
    () => (historyPage?.items ?? []).map((entry: DodHistoryEntry) => toHistoryRow(entry)),
    [historyPage],
  );

  const shownIds = useMemo(() => new Set(rows.map((row) => row.id)), [rows]);

  const readers = useMemo(() => historyPage?.readers ?? [], [historyPage]);

  /** The socket feed carries the RCP's name; the URL filter carries its id. */
  const rcpAliases = useMemo(
    () => (filters.rcp ? historyRcpAliases(filters.rcp, readers) : undefined),
    [filters.rcp, readers],
  );

  /**
   * Status is deliberately not published: this tab owns its own OK/ERROR select, and the toolbar's
   * shared status slot is used by tabs with a queue-state vocabulary.
   */
  useEffect(() => {
    setToolbarOptions(toHistoryToolbarOptions(readers, { rcp: filters.rcp, nlk: filters.nlk }));
  }, [readers, filters.rcp, filters.nlk, setToolbarOptions]);

  /**
   * The socket feed is the live source; it is counted, never spliced in. Appending would move
   * every row the reader is looking at, which is exactly what the "N new" button exists to avoid.
   */
  const pending = useMemo(
    () => pendingHistoryEntries(historyEntries, queryFilter, shownIds, flushedAt, rcpAliases),
    [historyEntries, queryFilter, shownIds, flushedAt, rcpAliases],
  );

  const onShowPending = useCallback(() => {
    setFlushedAt(Date.now());
    setPage(0);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_HISTORY_V2]);
  }, [queryClient]);

  const resetToFirstPage = useCallback(() => {
    setPage(0);
    setSelectedRow(null);
  }, []);

  /**
   * Memoised on purpose: it is a dependency of the `setDetails` effect below, and a fresh arrow on
   * every render would re-run that effect, re-render the shell and loop.
   */
  const onCloseDetails = useCallback(() => setSelectedRow(null), []);

  /**
   * The details panel belongs to the page, not to the tab: the shell renders it through
   * PageLayout's `renderDetails`, beside the table, exactly like the card and event pages.
   */
  useEffect(() => {
    setDetails(
      selectedRow ? (
        <DetailsDrawer isOpen header={formatMessage({ id: AppMessages['dod.history.details.title'] })}>
          <HistoryDetails row={selectedRow} onClose={onCloseDetails} />
        </DetailsDrawer>
      ) : null,
    );
  }, [setDetails, selectedRow, onCloseDetails, formatMessage]);

  useEffect(() => () => setDetails(null), [setDetails]);

  return (
    <HistoryTab
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      total={historyPage?.total ?? 0}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        resetToFirstPage();
      }}
      source={source}
      status={status}
      search={searchInput}
      onSourceChange={(next) => {
        setSource(next);
        resetToFirstPage();
      }}
      onStatusChange={(next) => {
        setStatus(next);
        resetToFirstPage();
      }}
      onSearchChange={(next) => {
        setSearchInput(next);
        resetToFirstPage();
      }}
      pendingCount={pending.length}
      onShowPending={onShowPending}
      isLive={isSocketConnected}
      selectedRow={selectedRow}
      onSelectRow={setSelectedRow}
      onExportCsv={() => setIsExporting(true)}
      isExporting={isExporting}
    />
  );
};
